import React from 'react';
import { FaTimes } from 'react-icons/fa';

interface Customer {
  id: number;
  name: string;
}

interface ProductItem {
  productId: number;
  productName: string;
  quantity: number;
  pricePerUnit: number;
}

interface Order {
  id: number;
  customer: Customer;
  orderDate: string;
  totalAmount: number;
  orderStatus: 'PENDING' | 'PROCESSING' | 'SHIPPED' | 'DELIVERED' | 'CANCELLED' | 'REFUND';
  paymentStatus: 'PAID' | 'UNPAID' | 'REFUND_PENDING' | 'REFUNDED';
  shippingStatus: 'READY_FOR_DISPATCH' | 'IN_TRANSIT' | 'OUT_FOR_DELIVERY' | 'DELIVERED';
  deliveryAddress: string;
  products: ProductItem[];
}

interface OrderDetailsModalProps {
  order: Order;
  onClose: () => void;
}

const ViewOrderDetails: React.FC<OrderDetailsModalProps> = ({ order, onClose }) => {
  if (!order) return null;

  const getStatusBadge = (status: string, type: 'order' | 'payment' | 'shipping') => {
    // Dark mode logic: Dark text on light bg looks okay, but muted looks better. 
    // Keeping simple logic but ensuring text is readable.
    let colorClass = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'; // Default
    switch (type) {
      case 'order':
        switch (status) {
          case 'DELIVERED': colorClass = 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'; break;
          case 'SHIPPED': colorClass = 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'; break;
          case 'PROCESSING': colorClass = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'; break;
          case 'PENDING': colorClass = 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'; break;
          case 'CANCELLED': colorClass = 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'; break;
          case 'REFUND': colorClass = 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200'; break;
        }
        break;
      case 'payment':
        switch (status) {
          case 'PAID': colorClass = 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'; break;
          case 'UNPAID': colorClass = 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'; break;
          case 'REFUND_PENDING': colorClass = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'; break;
          case 'REFUNDED': colorClass = 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200'; break;
        }
        break;
      case 'shipping':
        switch (status) {
          case 'DELIVERED': colorClass = 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'; break;
          case 'IN_TRANSIT': colorClass = 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'; break;
          case 'OUT_FOR_DELIVERY': colorClass = 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'; break;
          case 'READY_FOR_DISPATCH': colorClass = 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'; break;
        }
        break;
    }
    return (
      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${colorClass}`}>
        {status.replace(/_/g, ' ')}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      {/* Modal Container: Added dark:bg-gray-800 and dark:border-gray-700 */}
      <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-2xl max-w-2xl w-full border border-gray-300 dark:border-gray-700 transform transition-all duration-300 scale-100 ease-out overflow-y-auto max-h-[90vh]">
        
        <div className="flex justify-between items-center mb-6">
          {/* Header Text */}
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Order Details  {order.id}</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white transition-colors"
            title="Close"
          >
            <FaTimes size={24} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-6 text-gray-700 dark:text-gray-300 mb-6">
          <div>
            <p className="font-semibold text-gray-800 dark:text-gray-100 mb-1">Customer Information:</p>
            <p><strong>Name:</strong> {order.customer.name}</p>
            <p><strong>Delivery Address:</strong> {order.deliveryAddress}</p>
          </div>
          <div>
            <p className="font-semibold text-gray-800 dark:text-gray-100 mb-1">Order Information:</p>
            <p><strong>Date & Time:</strong> {new Date(order.orderDate).toLocaleString()}</p>
            <p><strong>Total Amount:</strong> ₹{order.totalAmount.toFixed(2)}</p>
            <p className="flex items-center gap-2 mt-1"><strong>Order:</strong> {getStatusBadge(order.orderStatus, 'order')}</p>
            <p className="flex items-center gap-2 mt-1"><strong>Payment:</strong> {getStatusBadge(order.paymentStatus, 'payment')}</p>
            <p className="flex items-center gap-2 mt-1"><strong>Shipping:</strong> {getStatusBadge(order.shippingStatus, 'shipping')}</p>
          </div>
        </div>

        <div className="mb-6">
          <h4 className="text-lg font-semibold text-gray-800 dark:text-white mb-3">Products in Order:</h4>
          {order.products && order.products.length > 0 ? (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                {/* Table Header */}
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Product Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Quantity</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Price/Unit</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Subtotal</th>
                  </tr>
                </thead>
                {/* Table Body */}
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {order.products.map((item, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">{item.productName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{item.quantity}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">₹{item.pricePerUnit.toFixed(2)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">₹{(item.quantity * item.pricePerUnit).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-600 dark:text-gray-400">No products listed for this order.</p>
          )}
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-6 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors font-semibold shadow-md dark:bg-blue-600 dark:hover:bg-blue-500"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewOrderDetails;